import { useEffect, useRef, useState } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Copy, ExternalLink, Globe, Link2, Loader2, RotateCcw, Wrench } from 'lucide-react';
import {
  apiErrorMessage,
  captureService,
  type CaptureReplay,
  type ReplayReport,
  type ReplayReportFrame,
  type ReplayStatus,
  type ReplaySummary,
} from '@/services/captureApi';
import { cn } from '@/lib/utils';
import { AppActions } from './AppActions';
import { isReplayBuilding, matchPercent, replayChip, type ReplayChipTone } from './replayStatus';

// Replay code may not navigate the Captures page (no allow-top-navigation): an app's target=_top link or
// top.location would otherwise take the whole page away after a click. Its own origin keeps its service worker.
const REPLAY_SANDBOX = 'allow-scripts allow-same-origin allow-forms allow-modals allow-popups allow-downloads';

const CHIP_STYLE: Record<ReplayChipTone, { icon: typeof CheckCircle2; classes: string }> = {
  building: { icon: Loader2, classes: 'bg-primary/90 text-primary-foreground' },
  ready: { icon: CheckCircle2, classes: 'bg-emerald-500/90 text-white' },
  fix: { icon: Wrench, classes: 'bg-amber-500/90 text-white' },
  failed: { icon: AlertCircle, classes: 'bg-destructive/90 text-destructive-foreground' },
};

export function ReplayStatusChip({ status, summary }: { status: ReplayStatus; summary: ReplaySummary | null }) {
  const { label, tone } = replayChip(status, summary);
  const { icon: Icon, classes } = CHIP_STYLE[tone];
  return (
    <div className={cn(
      'absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold backdrop-blur-md',
      'shadow-sm ring-1 ring-black/10',
      classes,
    )}>
      <Icon className={cn('h-3 w-3', tone === 'building' && 'animate-spin')} />
      {label}
    </div>
  );
}

export function CaptureNotice({ icon: Icon, spin, title, children }: {
  icon: typeof Loader2; spin?: boolean; title: string; children?: React.ReactNode;
}) {
  return (
    <div className="flex-1 flex items-center justify-center p-6">
      <div className="max-w-md text-center flex flex-col items-center gap-3">
        <Icon className={cn('h-6 w-6 text-muted-foreground', spin && 'animate-spin text-primary')} />
        <p className="text-sm font-semibold text-foreground">{title}</p>
        {children && <div className="text-xs text-muted-foreground">{children}</div>}
      </div>
    </div>
  );
}

interface ReplayViewProps {
  captureId: string;
  captureName: string;
  replay: CaptureReplay | null;
  host: string;
  uploading: boolean;
  /** The steps that go into a project with the app. */
  stepIndices: number[];
  onOpenReport: () => void;
}

export function ReplayView({ captureId, captureName, replay, host, uploading, stepIndices, onOpenReport }: ReplayViewProps) {
  const [run, setRun] = useState(0);
  const [appError, setAppError] = useState<string | null>(null);

  if (!replay) return <CaptureNotice icon={Loader2} spin title="Loading the replay…" />;
  if (!replay.hosted) return <CaptureNotice icon={Globe} title="Replays aren't available on this server yet; the steps are on the Steps tab." />;
  const status = replay.replayStatus;

  if (!status && uploading) {
    return <CaptureNotice icon={Loader2} spin title="Waiting for the upload to finish…" />;
  }
  if (isReplayBuilding(status)) {
    return (
      <CaptureNotice icon={Loader2} spin title={status === 'queued' ? 'Waiting to build the replay…' : 'Building the replay…'}>
        This takes a few minutes. You can leave this page and come back.
      </CaptureNotice>
    );
  }
  if (status === 'failed') {
    return (
      <CaptureNotice icon={AlertCircle} title="The replay could not be built.">
        {replay.replayError}
      </CaptureNotice>
    );
  }
  if (!status) return <CaptureNotice icon={Globe} title="No replay for this capture yet." />;
  if (!replay.viewerUrl) {
    return (
      <CaptureNotice icon={AlertCircle} title="The replay is built, but it can't be shown here.">
        {replay.viewerUnavailable}
      </CaptureNotice>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {status === 'needs_fix' && (
        <div className="px-4 py-2 border-b border-amber-500/30 bg-amber-500/10 flex items-center gap-2 text-xs text-amber-700 dark:text-amber-300 shrink-0">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          <span>This replay needs a fix, so parts of it may not work.</span>
          <button type="button" onClick={onOpenReport} className="font-semibold underline underline-offset-2 hover:no-underline">
            See the report
          </button>
        </div>
      )}
      <div className="px-4 h-9 border-b border-border flex items-center gap-2 text-xs text-muted-foreground shrink-0 bg-card/50">
        <Globe className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">Replay of {host}. Nothing you do here reaches it.</span>
        <button
          type="button"
          onClick={() => setRun(r => r + 1)}
          className="ml-auto h-7 inline-flex items-center gap-1.5 rounded-md px-2 font-medium text-muted-foreground hover:text-foreground hover:bg-black/5 dark:hover:bg-white/5 transition shrink-0"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Start over
        </button>
        <AppActions
          captureId={captureId}
          captureName={captureName}
          stepIndices={stepIndices}
          onError={setAppError}
          needsFix={status === 'needs_fix' ? replay.replaySummary?.reasons ?? [] : null}
          onOpenReport={onOpenReport}
        />
      </div>
      <ReplayLink captureId={captureId} needsFix={status === 'needs_fix'} />
      {appError && (
        <div role="alert" className="px-4 py-2 border-b border-destructive/30 bg-destructive/10 flex items-center gap-2 text-xs text-destructive shrink-0">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          <span>{appError}</span>
        </div>
      )}
      <iframe
        key={run}
        src={replay.viewerUrl}
        title={`Replay of ${host}`}
        sandbox={REPLAY_SANDBOX}
        allow="clipboard-read; clipboard-write"
        className="flex-1 w-full border-0 bg-white"
      />
    </div>
  );
}

/**
 * A lasting link to the replay: the replay published as an app (one per build), opened in a new tab where anyone
 * holding the link can replay it. Made only when the person asks, and said plainly before they do.
 */
function ReplayLink({ captureId, needsFix }: { captureId: string; needsFix: boolean }) {
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pending = useRef<AbortController | null>(null);
  useEffect(() => () => pending.current?.abort(), []);

  async function make() {
    const signal = (pending.current = new AbortController()).signal;
    setBusy(true);
    setError(null);
    try {
      const app = needsFix
        ? await captureService.publishAppAndWait(captureId, signal, { anyway: true })
        : await captureService.publishAppAndWait(captureId, signal);
      if (!app.url) throw new Error('replay hosting is not set up');
      // The boot page, never the bare root: once the replay's service worker controls the origin it answers "/" as the
      // app's own page, which was never recorded. Boot reseeds the recorded state and opens the start screen, every time.
      setUrl(`${app.url}__replay/boot.html`);
    } catch (e) {
      if (!signal.aborted) setError(apiErrorMessage(e));
    } finally {
      if (!signal.aborted) setBusy(false);
    }
  }

  return (
    <div className="px-4 py-2 border-b border-border flex flex-wrap items-center gap-2 text-xs text-muted-foreground shrink-0">
      {url ? (
        <>
          <input
            readOnly
            aria-label="Replay link"
            value={url}
            onFocus={e => e.currentTarget.select()}
            className="h-7 min-w-0 flex-1 max-w-md rounded-md border border-border bg-background px-2 font-mono text-[11px] text-foreground"
          />
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="h-7 inline-flex items-center gap-1.5 rounded-md px-2 font-medium text-foreground hover:bg-black/5 dark:hover:bg-white/5 transition shrink-0"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open in a new tab
          </a>
          <button
            type="button"
            onClick={() => { void navigator.clipboard.writeText(url); }}
            className="h-7 inline-flex items-center gap-1.5 rounded-md px-2 font-medium text-foreground hover:bg-black/5 dark:hover:bg-white/5 transition shrink-0"
          >
            <Copy className="h-3.5 w-3.5" />
            Copy
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={make}
          disabled={busy}
          className="h-7 inline-flex items-center gap-1.5 rounded-md px-2 font-medium text-foreground hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-60 transition shrink-0"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />}
          {busy ? 'Making the link…' : needsFix ? 'Make a link anyway' : 'Make a link'}
        </button>
      )}
      <span data-testid="replay-link-note" className="min-w-0 flex-1">
        {url ? '' : 'Making a link publishes a copy of this replay. '}
        Anyone with the link can open it. It shows the recording's own data on a 4K Labs replay address, so keep it to
        people you'd show the recording to: don't embed it or post it publicly.
      </span>
      {error && <span role="alert" className="w-full text-destructive">{error}</span>}
    </div>
  );
}

const matchTone = (match: number) =>
  match >= 0.95 ? 'text-emerald-600 dark:text-emerald-400' : match >= 0.85 ? 'text-amber-600 dark:text-amber-400' : 'text-destructive';

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-card px-3.5 py-2.5 min-w-[120px]">
      <div className="text-lg font-semibold tabular-nums text-foreground">{value}</div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
    </div>
  );
}

function Picture({ src, caption, match, empty }: { src: string | null; caption: string; match?: number | null; empty: string }) {
  return (
    <figure className="min-w-0">
      <figcaption className="mb-1.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
        {caption}
        {match != null && <b className={cn('tabular-nums', matchTone(match))}>{matchPercent(match)}</b>}
      </figcaption>
      {src ? (
        <img src={src} alt={caption} loading="lazy" className="block w-full rounded-md border border-border" />
      ) : (
        <div className="rounded-md border border-dashed border-border py-10 text-center text-xs text-muted-foreground">{empty}</div>
      )}
    </figure>
  );
}

const frameLabel = (f: ReplayReportFrame) => f.label.charAt(0).toUpperCase() + f.label.slice(1);

export function ReportView({ captureId, buildId }: { captureId: string; buildId: string | null }) {
  const [report, setReport] = useState<ReplayReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!buildId) return;
    let cancelled = false;
    setReport(null);
    setError(null);
    captureService.getReplayReport(captureId)
      .then(r => { if (!cancelled) setReport(r); })
      .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : 'Unknown error'); });
    return () => { cancelled = true; };
  }, [captureId, buildId]);

  if (!buildId) return <CaptureNotice icon={Globe} title="The report appears once the replay is built." />;
  if (error) return <CaptureNotice icon={AlertCircle} title="Couldn't load the report.">{error}</CaptureNotice>;
  if (!report) return <CaptureNotice icon={Loader2} spin title="Loading the report…" />;

  const gaps = Object.entries(report.gaps).sort((a, b) => b[1] - a[1]);
  const pass = report.verdict.pass;

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-5xl mx-auto px-6 py-6 space-y-8">
        <section className="space-y-3">
          <p className={cn(
            'flex items-center gap-2 text-base font-semibold',
            pass ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400',
          )}>
            {pass ? <CheckCircle2 className="h-5 w-5" /> : <Wrench className="h-5 w-5" />}
            {pass ? 'Pass' : 'Needs a fix'}
          </p>
          {!pass && report.verdict.reasons.length > 0 && (
            <ul className="list-disc pl-5 space-y-1 text-sm text-foreground">
              {report.verdict.reasons.map(reason => <li key={reason}>{reason}</li>)}
            </ul>
          )}
          {(report.verdict.excused ?? []).map(step => (
            <p key={step.id} className="text-xs text-muted-foreground">
              Excused: {step.title} ({matchPercent(step.match)} alike): the capture took its picture while the app was still loading, and the replay shows more.
            </p>
          ))}
          <div className="flex flex-wrap gap-3">
            <Stat label="Median match" value={report.match.median != null ? matchPercent(report.match.median) : '—'} />
            <Stat label="Worst match" value={report.match.worst != null ? matchPercent(report.match.worst) : '—'} />
            <Stat label="Steps replayed" value={`${report.stepsOk}/${report.rows.length}`} />
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Steps</h2>
          {report.rows.map(row => (
            <article
              key={row.id}
              data-testid="report-step"
              className={cn('rounded-xl border bg-card p-4', row.action === 'failed' ? 'border-destructive/50' : 'border-border')}
            >
              <header className="flex flex-wrap items-baseline gap-2">
                <span className="text-[11px] font-mono font-semibold text-muted-foreground">{row.id}</span>
                <h3 className="text-sm font-semibold text-foreground">{row.title}</h3>
                {row.action === 'failed' && (
                  <span className="text-xs text-destructive">Not replayed{row.error ? `: ${row.error}` : ''}</span>
                )}
              </header>
              {row.frames.map(frame => (
                <div key={frame.label} className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Picture src={frame.capture} caption={`Capture · ${frameLabel(frame)}`} empty="No picture in the capture" />
                  <Picture src={frame.replay} caption={`Replay · ${frameLabel(frame)}`} match={frame.match} empty="No picture from the replay" />
                </div>
              ))}
            </article>
          ))}
        </section>

        <section className="space-y-2">
          <h2 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Requests the capture didn't have</h2>
          {gaps.length ? (
            <ul className="space-y-1">
              {gaps.map(([request, count]) => (
                <li key={request} className="flex items-center gap-2 text-xs">
                  <code className="min-w-0 truncate rounded bg-muted px-1.5 py-0.5 font-mono text-foreground" title={request}>{request}</code>
                  <span className="shrink-0 tabular-nums text-muted-foreground">×{count}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-muted-foreground">None. The capture answered every request the replay made.</p>
          )}
        </section>
      </div>
    </div>
  );
}
